import React, { useCallback, useMemo } from 'react';
import { LFC } from '../../Legacy';
import { ApiClient } from '../../api/ApiClient';
import { v2 } from '../../api/ApiInterfaceV2';
import { UserLiteData } from '../../api/ApiTypes';
import { CommandDispatcher } from '../../concepts/commands/CommandDispatcher';
import { useLoaderWithRefresh } from '../../platform/Hook';
import { InfoView } from '../../ui/InfoView';
import { LoadingGuard } from '../../ui/LoadingGuard';
import { useResolve } from '../Container';
import { Crash } from '../Crash';
import { PassportModel, UserRole } from '../PassportModel';
import { ProfileModel } from '../ProfileModel';
import { AccountModel } from '../account/AccountModel';
import { FlexFrame } from '../dashboard/elements/LayoutElements';
import { workspaceRemoveMember } from '../workspace/WorkspaceCommands';
import { userChangeRole, userRemove } from './ManageUsersCommands';

const { Section,
    LargeHeading, Heading, HeadingAction,
    DetailTable, DetailRow, DetailCell, DetailAction,
    ListItem, ListLabel, ListAction,
} = InfoView;

export const ManageUsersDetailView: LFC<{ user: UserLiteData }> = ({ user }) => {

    const account = useResolve(AccountModel);
    const api = useResolve(ApiClient);
    const dispatch = useResolve(CommandDispatcher);
    const passport = useResolve(PassportModel);
    const profile = useResolve(ProfileModel);

    const [loading, info, err, refresh] = useLoaderWithRefresh(
        async () => await api.exec(v2.admin.users.info(user.id)),
        [user]
    );

    const isMe = useMemo(() => profile.id === user.id, [profile.id, user.id]);

    const onChangeRoleClick = useCallback(async () => {
        if (!info) return;
        if (await dispatch(userChangeRole, [info.id])) {
            await refresh();
        }
    }, [info]);

    const onRemoveUserClick = useCallback(async () => {
        if (!info) return;
        await dispatch(userRemove, [info.id]);
    }, [info]);

    const onRemoveWorkspaceClick = useCallback(async (workspaceId: string) => {
        if (!info) return;
        if (await dispatch(workspaceRemoveMember, [info.id, workspaceId])) {
            await refresh();
        }
    }, [info]);

    if (err) return <Crash error={err} refreshOn403 />;

    return <FlexFrame>
        <InfoView>
            <LoadingGuard loading={loading}>
                <Section>
                    <LargeHeading>{info?.name} {isMe ? '(You)' : ''}</LargeHeading>
                </Section>
                <Section>
                    <Heading>
                        Information
                        <HeadingAction onClick={onRemoveUserClick}>Remove Person</HeadingAction>
                    </Heading>
                    <DetailTable>
                        <DetailRow>
                            <DetailCell shrink>Id:</DetailCell>
                            <DetailCell>{info?.id}</DetailCell>
                        </DetailRow>
                        <DetailRow>
                            <DetailCell shrink>Email:</DetailCell>
                            <DetailCell>{info?.email}</DetailCell>
                        </DetailRow>
                        <DetailRow>
                            <DetailCell shrink>Joined {account.label}:</DetailCell>
                            <DetailCell>
                                {info && `${info.joinedAt.toLocaleDateString()} @ ${info.joinedAt.toLocaleTimeString()}`}
                            </DetailCell>
                        </DetailRow>
                        <DetailRow>
                            <DetailCell shrink>Joined Ludi:</DetailCell>
                            <DetailCell>
                                {info && `${info.createdAt.toLocaleDateString()} @ ${info.createdAt.toLocaleTimeString()}`}
                            </DetailCell>
                        </DetailRow>
                        <DetailRow>
                            <DetailCell shrink>Role:</DetailCell>
                            <DetailCell>
                                {info && UserRole.lookup(info.roleName)?.label}
                                {info && !!UserRole.options(passport, info.roleName).length && !isMe &&
                                    <DetailAction onClick={loading ? null : () => onChangeRoleClick()}>Change Role</DetailAction>
                                }
                            </DetailCell>
                        </DetailRow>
                    </DetailTable>
                </Section>
                <Section>
                    <Heading>Workspaces</Heading>
                    {info && info.workspaces.map(x =>
                        <ListItem key={x.id}>
                            <ListLabel>{x.label}</ListLabel>
                            <ListAction onClick={() => onRemoveWorkspaceClick(x.id)}>
                                Remove
                            </ListAction>
                        </ListItem>
                    )}
                </Section>
            </LoadingGuard>
        </InfoView>
    </FlexFrame>;
};

